import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    useReactTable,
    getCoreRowModel,
    flexRender,
    type ColumnDef,
    type SortingState,
} from '@tanstack/react-table';
import { Download, Highlighter, Pencil } from 'lucide-react';
import { Badge } from './shared/Badge';
import { TaxonomyChip } from './shared/TaxonomyChip';
import { SearchFilterBar, type FilterPill } from './shared/SearchFilterBar';
import { FilterChip } from './shared/FilterChip';
import styled from 'styled-components';
import type { DocumentDate, DocumentOverviewItem, TaxonomyTreeNode } from '../types';
import type { PaginationModel } from '../types/table';
import { InlineEditableText } from './InlineEditableText';
import { OverflowTitle } from './OverflowTitle';
import {
    TableWrapper,
    StyledTable,
    Thead,
    Tbody,
    Th,
    Tr,
    Td,
    SortIndicator,
    EmptyRow,
    LoadingOverlay,
} from './shared/Table';
import { TablePagination } from './shared/TablePagination';
import { StatusDot, WarningChip as BaseWarningChip } from './shared/Badges';
import { colors, radii, transitions } from '../theme/tokens';
import { useInView } from '../hooks/useInView';
import { formatNumber, formatDocumentDate } from '../utils/format';
import { formatRelativeTime } from '../utils/time';
import { highlightMatches } from '../utils/highlight';
import { isInteractiveElement } from '../utils/interaction';
import { DocumentDatePicker } from './DocumentDatePicker';

export type DocumentListActionId = 'open' | 'rename' | 'export';

interface DocumentTableProps {
    documents: DocumentOverviewItem[];
    rowCount: number;
    paginationModel: PaginationModel;
    onPaginationModelChange: (model: PaginationModel) => void;
    searchQuery: string;
    onSearchQueryChange: (value: string) => void;
    sortOption: string;
    onSortOptionChange: (value: string) => void;
    activeStatus?: string | null;
    onStatusFilterChange?: (value: string | null) => void;
    /** Active category filter: a bare section or `section/leaf` (hierarchical). */
    activeCategory?: string | null;
    onCategoryFilterChange?: (value: string | null) => void;
    /** Checklist taxonomy tree (sections -> leaves) for the category dropdown. */
    categoryTree?: TaxonomyTreeNode[];
    dateRange?: DateRangeValue | null;
    onDateRangeChange?: (value: DateRangeValue | null) => void;
    selectedDocumentId?: string | null;
    onDocumentAction?: (action: DocumentListActionId, docId: string) => void;
    onDocumentRename?: (docId: string, title: string) => Promise<void> | void;
    disabledDocumentActions?: DocumentListActionId[];
    thumbnailsByDoc?: Record<string, (string | null)[]>;
    onRequestThumbnails?: (document: DocumentOverviewItem) => void;
    emptyStateMessage?: string;
    isLoading?: boolean;
    onWarningClick?: (warningId: string, docId: string) => void;
    tagPool?: string[];
    onTagsChange?: (docId: string, tags: string[]) => void | Promise<void>;
    onCreateTag?: (tag: string) => void | Promise<void>;
    onDocumentDateChange?: (docId: string, date: DocumentDate | null) => void | Promise<void>;
    duplicateFilterDocId?: string | null;
    onDuplicateFilterChange?: (docId: string | null) => void;
    /** Doc-ids currently selected for bulk actions (multi-doc merge). State
     *  is lifted to the parent so selection survives pagination, sort, and
     *  filter changes. Pass `undefined` to disable the selection UI. */
    selectedDocIds?: string[];
    /** Toggle selection for a single doc. `range: true` means shift-click — the
     *  table provides the visible doc-id sequence so the parent can compute
     *  the range from the last anchor. `only: true` is unused today but
     *  reserved for "click without modifier replaces selection" semantics if
     *  we want it later (currently a plain click *toggles*). */
    onToggleSelection?: (
        docId: string,
        opts: { range?: boolean; visibleDocIds: string[] },
    ) => void;
    /** Header-checkbox handler. `select=true` selects all visible (current
     *  page) rows; `false` deselects only the visible rows (preserving any
     *  off-page selections). */
    onToggleSelectAllOnPage?: (visibleDocIds: string[], select: boolean) => void;
}

interface DocumentRow {
    id: string;
    document: DocumentOverviewItem;
    title: string;
    status: string;
    pageCount: number;
    lowConfidencePages: number;
    lastEditedAt?: string;
    uploadDate?: string;
    sourceFilename?: string;
    confidence?: number | null;
    firstPageIndex?: number;
    pageRange?: string;
    documentDateValue?: number | null;
}

export interface DateRangeValue {
    start?: string;
    end?: string;
}

const PreviewFrame = styled.div`
    position: relative;
    width: 36px;
    aspect-ratio: 3 / 4;
    margin: 0 auto;
    overflow: hidden;
    border: 1px solid ${colors.border.subtle};
    background: ${colors.surface.subtle};
    display: flex;
    align-items: center;
    justify-content: center;
`;

const PreviewImage = styled.img`
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
`;

const PreviewSkeleton = styled.div`
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, ${colors.border.subtle} 0%, ${colors.surface.subtle} 50%, ${colors.border.subtle} 100%);
    animation: pulse 1.5s ease-in-out infinite;

    @keyframes pulse {
        0% { opacity: 0.7; }
        50% { opacity: 1; }
        100% { opacity: 0.7; }
    }
`;

const PreviewPlaceholder = styled.div`
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 4px;
    text-align: center;
    color: ${colors.text.muted};
    font-size: 11px;
`;


const WarningChip = styled(BaseWarningChip)`
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        border-color: ${colors.accents.primary};
        background: ${colors.surface.subtle};
    }
`;

const WarningsRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
`;

const Snippet = styled.div`
    margin: 2px 0 0;
    font-size: 12px;
    line-height: 1.45;
    color: ${colors.text.secondary};

    mark {
        background: rgba(250, 204, 21, 0.45);
        padding: 0 2px;
        border-radius: 4px;
    }
`;

const SnippetText = styled.span`
    display: inline;
`;

const OcrHitsInline = styled.span`
    margin-left: 6px;
    color: ${colors.status.info};
    font-weight: 500;
    display: inline-flex;
    align-items: center;
    gap: 4px;
`;

const SelectCheckbox = styled.input`
    cursor: pointer;
    width: 16px;
    height: 16px;
    margin: 0;
    accent-color: #111;
`;

const ExportButton = styled.button<{ $floating?: boolean }>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 6px;
    min-width: 32px;
    min-height: 32px;
    border: none;
    border-radius: ${radii.pill};
    background: transparent;
    color: ${colors.accents.primaryDark};
    cursor: pointer;
    transition: color ${transitions.default}, background ${transitions.default};

    &:hover {
        background: rgba(59, 130, 246, 0.12);
    }

    &:disabled {
        opacity: 0.4;
        cursor: not-allowed;
    }

    &:disabled:hover {
        background: transparent;
    }

    &:focus-visible {
        outline: 2px solid rgba(59, 130, 246, 0.45);
        outline-offset: 2px;
    }

    ${({ $floating }) =>
        $floating &&
        `
          position: absolute;
          top: 10px;
          right: 10px;
        `};
`;



const EmptyOverlay = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 48px 16px;
    color: ${colors.text.muted};
    font-size: 14px;
`;

const TitleContainer = styled.div`
    display: inline-flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
    min-width: 0;
    font-size: 13px;
    color: #111;
    cursor: text;
`;

const DatePickerButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 0;
    padding: 0;
    border: none;
    background: none;
    color: #999;
    font-size: 12px;
    font-family: inherit;
    cursor: pointer;

    &:hover {
        color: #555;
        text-decoration: underline;
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;


const ToolbarArea = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
`;

const ToolbarRight = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-left: auto;
`;

type ViewMode = 'compact' | 'full';

const ViewToggleGroup = styled.div`
    display: flex;
    border-radius: 6px;
    overflow: hidden;
    border: 1px solid #e0e0e0;
`;

const ViewToggleButton = styled.button<{ $active: boolean }>`
    padding: 5px 8px;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    background: ${({ $active }) => $active ? '#111' : '#fff'};
    color: ${({ $active }) => $active ? '#fff' : '#999'};
    font-family: inherit;
`;

const ColumnMenuWrapper = styled.div`
    position: relative;
`;

const ColumnMenuButton = styled.button<{ $open: boolean }>`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 5px 10px;
    border-radius: 6px;
    border: 1px solid #e0e0e0;
    background: ${({ $open }) => $open ? '#f5f5f5' : '#fff'};
    font-size: 11px;
    font-weight: 500;
    color: #666;
    cursor: pointer;
    font-family: inherit;
`;

const ColumnMenuPanel = styled.div`
    position: absolute;
    top: calc(100% + 4px);
    right: 0;
    z-index: 11;
    background: #fff;
    border: 1px solid #e8e8e8;
    border-radius: 8px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
    padding: 6px 4px;
    width: 180px;
`;

const ColumnMenuItem = styled.div<{ $locked: boolean }>`
    padding: 6px 10px;
    border-radius: 5px;
    cursor: ${({ $locked }) => $locked ? 'default' : 'pointer'};
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    color: ${({ $locked }) => $locked ? '#ccc' : '#333'};

    &:hover {
        background: ${({ $locked }) => $locked ? 'transparent' : '#f5f5f5'};
    }
`;

const ColumnCheckbox = styled.div<{ $checked: boolean; $locked: boolean }>`
    width: 14px;
    height: 14px;
    border-radius: 3px;
    flex-shrink: 0;
    border: ${({ $checked }) => $checked ? 'none' : '1.5px solid #ddd'};
    background: ${({ $checked }) => $checked ? '#111' : '#fff'};
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: ${({ $locked }) => $locked ? 0.4 : 1};
`;

const CompactRow = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 12px;
    cursor: pointer;
    border-bottom: 1px solid #f8f8f8;

    &:hover {
        background: #fafafa;
    }
`;

const CompactDot = styled.span<{ $reviewed: boolean }>`
    width: 6px;
    height: 6px;
    border-radius: 50%;
    flex-shrink: 0;
    background: ${({ $reviewed }) => $reviewed ? '#22c55e' : '#d4d4d4'};
`;

const CompactTitle = styled.span`
    font-size: 13px;
    font-weight: 500;
    color: #111;
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;


const CompactPages = styled.span`
    font-size: 12px;
    color: #999;
    font-variant-numeric: tabular-nums;
    flex-shrink: 0;
    min-width: 40px;
    text-align: right;
`;

const COLUMN_CONFIG = [
    { id: 'title', label: 'Title', locked: true },
    { id: 'preview', label: 'Preview', locked: false },
    { id: 'taxonomy', label: 'Category', locked: false },
    { id: 'status', label: 'Status', locked: false },
    { id: 'pageCount', label: 'Pages', locked: false },
    { id: 'pageRange', label: 'Source pages', locked: false },
    { id: 'documentDate', label: 'Date', locked: false },
    { id: 'uploadDate', label: 'Uploaded', locked: false },
    { id: 'sourceFilename', label: 'Source file', locked: false },
    { id: 'lastEditedAt', label: 'Updated', locked: false },
    { id: 'export', label: 'Export PDF', locked: false },
] as const;

// Columns hidden by default (no stored preference yet). Title is locked-on; the
// out-of-the-box set is Title + Category + Pages + Date. Preview (thumbnails),
// Status, Source pages, Updated, and Export start hidden but stay toggleable via
// the column menu, and the choice persists in localStorage.
const DEFAULT_HIDDEN_COLUMNS: readonly string[] = [
    'preview', 'status', 'pageRange', 'uploadDate', 'sourceFilename', 'lastEditedAt', 'export',
];

// Render a document date at its stored precision ("2024" / "Mar 2024" /
// "Mar 15, 2024"), never padding a missing day/month. Delegates to the shared
// precision-aware formatter; returns null for an absent/no-date value (the table
// shows nothing rather than "No date" in the compact cell).
const formatShortDocumentDate = (date?: DocumentDate): string | null => {
    if (!date || date.noDate) {
        return null;
    }
    return formatDocumentDate(date);
};

// Upload dates are ISO timestamp strings (Filevine uploadDate); the table
// shows just the calendar date.
const formatUploadDate = (iso?: string): string | null => {
    const match = iso?.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) {
        return null;
    }
    const [, year, month, day] = match;
    const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
    return date.toLocaleDateString(undefined, {
        year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC',
    });
};

const DatePickerCell: React.FC<{
    row: DocumentRow;
    onDateChange?: (docId: string, date: DocumentDate | null) => void | Promise<void>;
}> = ({ row, onDateChange }) => {
    const [isSaving, setIsSaving] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const anchorRef = useRef<HTMLButtonElement>(null);

    // The table cell now opens the SAME shared popup as the inspector, so a
    // table edit can set precision + "no date" too (full convergence — the old
    // precision-locked quick-edit behavior is gone).
    const handleCommit = async (next: DocumentDate | null) => {
        if (!onDateChange || isSaving) {
            return;
        }
        setIsSaving(true);
        try {
            await onDateChange(row.document.docId, next);
        } finally {
            setIsSaving(false);
        }
    };

    const hasDate = Boolean(row.document.date && !row.document.date.noDate
        && (row.document.date.normalized || row.document.date.year));
    // Display at stored precision (never padded to a fabricated day).
    const dateLabel = row.document.date ? formatShortDocumentDate(row.document.date) : null;

    return (
        <div
            data-stop-propagation="true"
            onClick={event => event.stopPropagation()}
            style={{ width: '100%', display: 'flex', alignItems: 'center' }}
        >
            <DatePickerButton
                ref={anchorRef}
                type="button"
                onClick={() => setIsOpen(true)}
                disabled={isSaving}
                aria-label={`Edit date for ${row.document.title}`}
            >
                {hasDate ? (dateLabel ?? 'Set date') : <Pencil size={12} />}
            </DatePickerButton>
            <DocumentDatePicker
                date={row.document.date}
                open={isOpen}
                onOpen={() => setIsOpen(true)}
                onClose={() => setIsOpen(false)}
                anchorEl={anchorRef.current}
                disabled={isSaving}
                onChange={handleCommit}
            />
        </div>
    );
};

const documentDateSortValue = (date?: DocumentDate): number | null => {
    if (!date) {
        return null;
    }

    if (date.normalized) {
        const parsed = Date.parse(date.normalized);
        if (!Number.isNaN(parsed)) {
            return parsed;
        }
    }

    if (typeof date.year === 'number') {
        const monthIndex =
            typeof date.month === 'number' && date.month >= 1 && date.month <= 12 ? date.month - 1 : 0;
        const day = typeof date.day === 'number' && date.day >= 1 && date.day <= 31 ? date.day : 1;
        const parsed = Date.UTC(date.year, monthIndex, day);
        if (!Number.isNaN(parsed)) {
            return parsed;
        }
    }

    if (date.raw) {
        const parsed = Date.parse(date.raw);
        if (!Number.isNaN(parsed)) {
            return parsed;
        }
    }

    return null;
};

const statusOptions = [
    { value: 'unreviewed', label: 'Unreviewed' },
    { value: 'in-progress', label: 'In progress' },
    { value: 'reviewed', label: 'Reviewed' },
    { value: 'edited', label: 'Needs attention' },
];

const sortOptionToModel = (option: string): SortingState => {
    // Parse option format: "field_direction" or legacy "field" format
    const parts = option.split('_');
    const field = parts[0];
    const direction = parts[1] as 'asc' | 'desc' | undefined;

    // Map field names to SortingState
    switch (field) {
        case 'title':
            return [{ id: 'title', desc: (direction ?? 'asc') === 'desc' }];
        case 'page':
        case 'pageCount':
            return [{ id: 'pageCount', desc: (direction ?? 'desc') === 'desc' }];
        case 'status':
            return [{ id: 'status', desc: (direction ?? 'asc') === 'desc' }];
        case 'taxonomy':
            return [{ id: 'taxonomy', desc: (direction ?? 'asc') === 'desc' }];
        case 'lowConfidence':
            return [{ id: 'lowConfidencePages', desc: (direction ?? 'desc') === 'desc' }];
        case 'confidence':
            return [{ id: 'confidence', desc: (direction ?? 'desc') === 'desc' }];
        case 'last':
        case 'lastEditedAt':
            return [{ id: 'lastEditedAt', desc: (direction ?? 'desc') === 'desc' }];
        case 'uploadDate':
            return [{ id: 'uploadDate', desc: (direction ?? 'desc') === 'desc' }];
        case 'date':
        case 'documentDate':
            return [{ id: 'documentDate', desc: (direction ?? 'desc') === 'desc' }];
        case 'pageRange':
        case 'pdfPages':
            return [{ id: 'pageRange', desc: (direction ?? 'asc') === 'desc' }];
        case 'nominal':
        case 'firstPageIndex':
            return [{ id: 'firstPageIndex', desc: (direction ?? 'asc') === 'desc' }];
        default:
            // Default to nominal sort (by PDF page order)
            return [{ id: 'firstPageIndex', desc: false }];
    }
};

const sortModelToOption = (model: SortingState): string => {
    if (!model.length) {
        return 'nominal';
    }
    const [item] = model;
    const direction = item.desc ? 'desc' : 'asc';

    // Return format: "field_direction" to preserve both field and direction
    if (item.id === 'title') return `title_${direction}`;
    if (item.id === 'pageCount') return `pageCount_${direction}`;
    if (item.id === 'status') return `status_${direction}`;
    if (item.id === 'taxonomy') return `taxonomy_${direction}`;
    if (item.id === 'lowConfidencePages') return `lowConfidence_${direction}`;
    if (item.id === 'confidence') return `confidence_${direction}`;
    if (item.id === 'documentDate') return `documentDate_${direction}`;
    if (item.id === 'uploadDate') return `uploadDate_${direction}`;
    if (item.id === 'lastEditedAt') return `lastEditedAt_${direction}`;
    if (item.id === 'pageRange') return `pageRange_${direction}`;
    if (item.id === 'firstPageIndex') return `firstPageIndex_${direction}`;
    return 'nominal';
};

const DocumentPreviewCell: React.FC<{
    document: DocumentOverviewItem;
    thumbnailsByDoc?: Record<string, (string | null)[]>;
    onRequestThumbnails?: (document: DocumentOverviewItem) => void;
}> = ({ document, thumbnailsByDoc, onRequestThumbnails }) => {
    const [hasRequested, setHasRequested] = useState(false);
    const { ref, inView } = useInView<HTMLDivElement>({
        rootMargin: '200px 0px',
    });

    const cachedThumbnails = thumbnailsByDoc?.[document.docId];
    const previewSrc = useMemo(
        () => (cachedThumbnails ?? document.thumbnailUrls)?.find(url => !!url) ?? null,
        [cachedThumbnails, document.thumbnailUrls],
    );

    useEffect(() => {
        setHasRequested(false);
    }, [document.docId]);

    useEffect(() => {
        const hasRefs = Array.isArray(document.thumbnailRefs) && document.thumbnailRefs.length > 0;
        const hasPreview = cachedThumbnails?.some(url => !!url);
        if (!inView || hasRequested || !onRequestThumbnails || !hasRefs || hasPreview) {
            return;
        }
        onRequestThumbnails(document);
        setHasRequested(true);
    }, [cachedThumbnails, document, hasRequested, inView, onRequestThumbnails]);

    return (
        <div
            ref={ref}
            style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
            <PreviewFrame aria-label={`Preview for ${document.title}`}>
                {previewSrc ? (
                    <PreviewImage src={previewSrc} alt={`Preview page for ${document.title}`} />
                ) : hasRequested ? (
                    <PreviewSkeleton />
                ) : (
                    <PreviewPlaceholder>No preview</PreviewPlaceholder>
                )}
            </PreviewFrame>
        </div>
    );
};

const DocumentTitleCell: React.FC<{
    row: DocumentRow;
    disabledSet: Set<DocumentListActionId>;
    onRename?: (docId: string, title: string) => Promise<void> | void;
    onAction?: (action: DocumentListActionId, docId: string) => void;
    searchQuery: string;
    onWarningClick?: (warningId: string, docId: string) => void;
}> = ({ row, disabledSet, onRename, onAction, searchQuery, onWarningClick }) => {
    const { document } = row;
    const renameDisabled = disabledSet.has('rename') || !onRename;
    const [isEditingTitle, setIsEditingTitle] = useState(false);
    const [isSubmittingRename, setIsSubmittingRename] = useState(false);

    useEffect(() => {
        if (renameDisabled && isEditingTitle) {
            setIsEditingTitle(false);
        }
    }, [renameDisabled, isEditingTitle]);

    const snippetHtml = useMemo(() => {
        if (!document.searchSnippet) {
            return '';
        }
        return highlightMatches(document.searchSnippet, searchQuery);
    }, [document.searchSnippet, searchQuery]);

    const ocrHitCount = document.ocrHitCount ?? null;

    const handleRenameSubmit = useCallback(
        async (nextTitle: string) => {
            if (!onRename) {
                setIsEditingTitle(false);
                return;
            }
            const trimmed = nextTitle.trim();
            if (!trimmed || trimmed === document.title.trim()) {
                setIsEditingTitle(false);
                return;
            }
            setIsSubmittingRename(true);
            try {
                await onRename(document.docId, trimmed);
                setIsEditingTitle(false);
            } catch (error) {
                console.error('Rename failed', error);
            } finally {
                setIsSubmittingRename(false);
            }
        },
        [document.docId, document.title, onRename],
    );

    return (
        <div>
            <TitleContainer>
                {isEditingTitle && onRename ? (
                    <div onClick={event => event.stopPropagation()} onKeyDown={event => event.stopPropagation()}>
                        <InlineEditableText
                            value={document.title}
                            onSubmit={handleRenameSubmit}
                            onCancel={() => setIsEditingTitle(false)}
                            isSaving={isSubmittingRename}
                            autoFocus
                            ariaLabel={`Rename ${document.title}`}
                        />
                    </div>
                ) : (
                    <button
                        type="button"
                        onClick={event => {
                            event.stopPropagation();
                            setIsEditingTitle(true);
                        }}
                        onKeyDown={event => event.stopPropagation()}
                        style={{
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            margin: 0,
                            cursor: renameDisabled ? 'default' : 'text',
                            color: 'inherit',
                            display: 'inline-flex',
                            minWidth: 0,
                        }}
                        disabled={renameDisabled}
                        aria-label={`Rename ${document.title}`}
                    >
                        <OverflowTitle title={document.title} variant="document-list" />
                    </button>
                )}
            </TitleContainer>

            {document.searchSnippet && snippetHtml && (
                <Snippet>
                    <SnippetText dangerouslySetInnerHTML={{ __html: snippetHtml }} />
                    {typeof ocrHitCount === 'number' && ocrHitCount > 0 && (
                        <OcrHitsInline>
                            <Highlighter size={14} />
                            {ocrHitCount === 1 ? '1 OCR hit' : `${ocrHitCount} OCR hits`}
                        </OcrHitsInline>
                    )}
                </Snippet>
            )}

        </div>
    );
};

const NoRows: React.FC<{ message?: string }> = ({ message }) => (
    <EmptyOverlay>{message ?? 'No documents to show.'}</EmptyOverlay>
);

export const DocumentTable: React.FC<DocumentTableProps> = ({
    documents,
    rowCount,
    paginationModel,
    onPaginationModelChange,
    searchQuery,
    onSearchQueryChange,
    sortOption,
    onSortOptionChange,
    activeStatus = null,
    onStatusFilterChange,
    activeCategory = null,
    onCategoryFilterChange,
    categoryTree = [],
    dateRange,
    onDateRangeChange,
    selectedDocumentId,
    onDocumentAction,
    onDocumentRename,
    disabledDocumentActions,
    thumbnailsByDoc,
    onRequestThumbnails,
    emptyStateMessage,
    isLoading = false,
    onWarningClick,
    tagPool,
    onTagsChange,
    onCreateTag,
    onDocumentDateChange,
    duplicateFilterDocId,
    onDuplicateFilterChange,
    selectedDocIds,
    onToggleSelection,
    onToggleSelectAllOnPage,
}) => {
    const [viewMode, setViewMode] = useState<ViewMode>(() => {
        try { return (localStorage.getItem('bigpdf:viewMode') as ViewMode) || 'full'; }
        catch { return 'full'; }
    });
    const [columnMenuOpen, setColumnMenuOpen] = useState(false);
    const [statusPopoverOpen, setStatusPopoverOpen] = useState(false);
    const [categoryPopoverOpen, setCategoryPopoverOpen] = useState(false);
    const [datePopoverOpen, setDatePopoverOpen] = useState(false);

    // Resolve a category value to its human label, for the pill. The taxonomy is
    // flat, so this is a direct lookup. Falls back to the raw value if the tree
    // hasn't loaded.
    const categoryLabel = useMemo(() => {
        if (!activeCategory) return null;
        const match = categoryTree.find(section => section.value === activeCategory);
        return match ? match.label : activeCategory;
    }, [activeCategory, categoryTree]);

    const filterPills = useMemo<FilterPill[]>(() => {
        const pills: FilterPill[] = [];
        if (activeStatus) {
            const opt = statusOptions.find(o => o.value === activeStatus);
            pills.push({ id: 'status', label: 'status:', value: opt?.label ?? activeStatus });
        }
        if (activeCategory) {
            pills.push({ id: 'category', label: 'category:', value: categoryLabel ?? activeCategory });
        }
        if (dateRange?.start || dateRange?.end) {
            const parts = [dateRange?.start, dateRange?.end].filter(Boolean);
            pills.push({ id: 'date', label: 'date:', value: parts.join(' – ') });
        }
        if (duplicateFilterDocId) {
            const title = documents.find(d => d.docId === duplicateFilterDocId)?.title ?? duplicateFilterDocId;
            pills.push({ id: 'duplicates', label: 'duplicates:', value: title, color: 'warning' });
        }
        return pills;
    }, [activeStatus, activeCategory, categoryLabel, dateRange, duplicateFilterDocId, documents]);

    const handleRemovePill = useCallback((id: string) => {
        if (id === 'status') onStatusFilterChange?.(null);
        if (id === 'category') onCategoryFilterChange?.(null);
        if (id === 'date') onDateRangeChange?.(null);
        if (id === 'duplicates') onDuplicateFilterChange?.(null);
    }, [onStatusFilterChange, onCategoryFilterChange, onDateRangeChange, onDuplicateFilterChange]);
    const [hiddenColumns, setHiddenColumns] = useState<Set<string>>(() => {
        try {
            const stored = localStorage.getItem('bigpdf:hiddenColumns');
            // No stored preference -> apply the default hidden set. A stored value
            // (even an empty list) is the user's own choice and wins.
            return stored ? new Set(JSON.parse(stored)) : new Set(DEFAULT_HIDDEN_COLUMNS);
        } catch { return new Set(DEFAULT_HIDDEN_COLUMNS); }
    });

    const selectionEnabled = selectedDocIds != null;
    const columnVisibility = useMemo(() => {
        const vis: Record<string, boolean> = {};
        for (const col of COLUMN_CONFIG) {
            vis[col.id] = !hiddenColumns.has(col.id);
        }
        // select column visible only when the parent provides selection state
        vis['select'] = selectionEnabled;
        return vis;
    }, [hiddenColumns, selectionEnabled]);

    const handleViewModeChange = useCallback((mode: ViewMode) => {
        setViewMode(mode);
        try { localStorage.setItem('bigpdf:viewMode', mode); } catch {}
    }, []);

    const toggleColumn = useCallback((id: string) => {
        setHiddenColumns(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            try { localStorage.setItem('bigpdf:hiddenColumns', JSON.stringify(Array.from(next))); } catch {}
            return next;
        });
    }, []);

    const disabledSet = useMemo(() => new Set(disabledDocumentActions ?? []), [disabledDocumentActions]);
    const disabledSetRef = useRef(disabledSet);
    useEffect(() => {
        disabledSetRef.current = disabledSet;
    }, [disabledSet]);

    const thumbnailsByDocRef = useRef(thumbnailsByDoc);
    useEffect(() => {
        thumbnailsByDocRef.current = thumbnailsByDoc;
    }, [thumbnailsByDoc]);

    const onDocumentActionRef = useRef(onDocumentAction);
    useEffect(() => {
        onDocumentActionRef.current = onDocumentAction;
    }, [onDocumentAction]);

    const onDocumentRenameRef = useRef(onDocumentRename);
    useEffect(() => {
        onDocumentRenameRef.current = onDocumentRename;
    }, [onDocumentRename]);

    const onRequestThumbnailsRef = useRef(onRequestThumbnails);
    useEffect(() => {
        onRequestThumbnailsRef.current = onRequestThumbnails;
    }, [onRequestThumbnails]);

    const selectedDocIdSet = useMemo(
        () => new Set(selectedDocIds ?? []),
        [selectedDocIds],
    );
    const selectedDocIdSetRef = useRef(selectedDocIdSet);
    useEffect(() => {
        selectedDocIdSetRef.current = selectedDocIdSet;
    }, [selectedDocIdSet]);

    const onToggleSelectionRef = useRef(onToggleSelection);
    useEffect(() => {
        onToggleSelectionRef.current = onToggleSelection;
    }, [onToggleSelection]);

    const onWarningClickRef = useRef(onWarningClick);
    useEffect(() => {
        onWarningClickRef.current = onWarningClick;
    }, [onWarningClick]);

    const onTagsChangeRef = useRef(onTagsChange);
    useEffect(() => {
        onTagsChangeRef.current = onTagsChange;
    }, [onTagsChange]);

    const onCreateTagRef = useRef(onCreateTag);
    useEffect(() => {
        onCreateTagRef.current = onCreateTag;
    }, [onCreateTag]);

    const onDocumentDateChangeRef = useRef(onDocumentDateChange);
    useEffect(() => {
        onDocumentDateChangeRef.current = onDocumentDateChange;
    }, [onDocumentDateChange]);

    const searchQueryRef = useRef(searchQuery);
    useEffect(() => {
        searchQueryRef.current = searchQuery;
    }, [searchQuery]);

    const rows = useMemo<DocumentRow[]>(
        () =>
            documents.map(document => ({
                id: document.docId,
                document,
                title: document.title,
                status: document.status,
                pageCount: document.pageCount,
                lowConfidencePages: document.lowConfidencePages ?? 0,
                lastEditedAt: document.lastEditedAt,
                uploadDate: document.uploadDate,
                sourceFilename: document.sourceFilename,
                confidence: document.confidence,
                firstPageIndex: document.firstPageIndex,
                documentDateValue: documentDateSortValue(document.date),
                pageRange: (() => {
                    // Source pages: position WITHIN the source PDF (per-source),
                    // from the backend. Falls back to the whole-case span only
                    // for legacy data that predates source_page_range.
                    if (document.sourcePageRange) {
                        return document.sourcePageRange;
                    }
                    const first = document.firstPageIndex != null ? document.firstPageIndex + 1 : null;
                    const last = first != null ? first + document.pageCount - 1 : null;
                    if (first != null && last != null) {
                        return `${first}\u2013${last}`;
                    }
                    return undefined;
                })(),
            })),
        [documents],
    );

    const sorting = useMemo(() => sortOptionToModel(sortOption), [sortOption]);

    const handleSortingChange = useCallback(
        (updaterOrValue: SortingState | ((prev: SortingState) => SortingState)) => {
            const nextSorting = typeof updaterOrValue === 'function' ? updaterOrValue(sorting) : updaterOrValue;
            const nextOption = sortModelToOption(nextSorting);
            if (nextOption !== sortOption) {
                onSortOptionChange(nextOption);
            } else if (!nextSorting.length && sortOption !== 'nominal') {
                onSortOptionChange('nominal');
            }
        },
        [onSortOptionChange, sortOption, sorting],
    );

    const handleRowClick = useCallback(
        (row: DocumentRow, event?: React.MouseEvent<HTMLElement>) => {
            if (disabledSetRef.current.has('open')) {
                return;
            }
            if (event) {
                const container = event.currentTarget as HTMLElement;
                if (isInteractiveElement(event.target as HTMLElement, container)) {
                    return;
                }
            }
            onDocumentActionRef.current?.('open', row.document.docId);
        },
        [],
    );

    const columns = useMemo<ColumnDef<DocumentRow, any>[]>(
        () => [
            {
                id: 'select',
                header: ({ table }) => {
                    const visibleDocIds = table.getRowModel().rows.map(r => r.original.document.docId);
                    const sel = selectedDocIdSetRef.current;
                    const visibleSelectedCount = visibleDocIds.reduce(
                        (acc, id) => acc + (sel.has(id) ? 1 : 0),
                        0,
                    );
                    const allSelected = visibleDocIds.length > 0 && visibleSelectedCount === visibleDocIds.length;
                    const someSelected = visibleSelectedCount > 0 && !allSelected;
                    return (
                        <SelectCheckbox
                            type="checkbox"
                            aria-label={allSelected ? 'Deselect all on this page' : 'Select all on this page'}
                            title={allSelected ? 'Deselect all on this page' : 'Select all on this page'}
                            checked={allSelected}
                            ref={(el) => { if (el) el.indeterminate = someSelected; }}
                            onChange={(event) => {
                                onToggleSelectAllOnPage?.(visibleDocIds, event.target.checked);
                            }}
                            onClick={(event) => event.stopPropagation()}
                        />
                    );
                },
                enableSorting: false,
                cell: ({ row, table }) => {
                    const docId = row.original.document.docId;
                    const checked = selectedDocIdSetRef.current.has(docId);
                    return (
                        <SelectCheckbox
                            type="checkbox"
                            aria-label={checked ? `Deselect ${row.original.document.title}` : `Select ${row.original.document.title}`}
                            checked={checked}
                            onClick={(event) => event.stopPropagation()}
                            onChange={(event) => {
                                const visibleDocIds = table.getRowModel().rows.map(r => r.original.document.docId);
                                const isShift = (event.nativeEvent as MouseEvent).shiftKey === true;
                                onToggleSelectionRef.current?.(docId, {
                                    range: isShift,
                                    visibleDocIds,
                                });
                            }}
                        />
                    );
                },
            },
            {
                id: 'preview',
                header: 'Preview',
                enableSorting: false,
                cell: ({ row }) => (
                    <DocumentPreviewCell
                        document={row.original.document}
                        thumbnailsByDoc={thumbnailsByDocRef.current}
                        onRequestThumbnails={onRequestThumbnailsRef.current ?? undefined}
                    />
                ),
            },
            {
                id: 'title',
                accessorKey: 'title',
                header: 'Title',
                cell: ({ row }) => (
                    <DocumentTitleCell
                        row={row.original}
                        disabledSet={disabledSetRef.current}
                        onRename={onDocumentRenameRef.current ?? undefined}
                        onAction={onDocumentActionRef.current ?? undefined}
                        searchQuery={searchQueryRef.current}
                        onWarningClick={onWarningClickRef.current ?? undefined}
                    />
                ),
            },
            {
                id: 'taxonomy',
                accessorKey: 'taxonomy',
                header: 'Category',
                // Sorting is server-side by canonical taxonomy rank (sort=taxonomy_*).
                cell: ({ row }) => <TaxonomyChip taxonomy={row.original.document.taxonomy} />,
            },
            {
                id: 'status',
                accessorKey: 'status',
                header: 'Status',
                cell: ({ row }) => {
                    const doc = row.original.document;
                    const s = doc.status;
                    const label = s === 'reviewed' ? 'Reviewed' : s === 'in-progress' ? 'In progress' : s === 'edited' ? 'Needs attention' : 'Unreviewed';
                    const lowConf = doc.lowConfidencePages ?? 0;
                    const showMerge = doc.trailingBoundaryUncertain;
                    // Duplicate-document badge intentionally hidden (detection + the
                    // duplicates filter/view still work; only the badge is gone).
                    return (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            <StatusDot $status={s}>{label}</StatusDot>
                            {(lowConf > 0 || showMerge) && (
                                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                                    {lowConf > 0 && <Badge label={`${lowConf} boundary`} type="quality" />}
                                    {showMerge && <Badge label="Merge" type="quality" />}
                                </div>
                            )}
                        </div>
                    );
                },
            },
            {
                id: 'pageCount',
                accessorKey: 'pageCount',
                header: 'Pages',
                meta: { align: 'right' as const, numeric: true },
                cell: ({ row }) => formatNumber(row.original.document.pageCount),
            },
            {
                id: 'pageRange',
                accessorKey: 'pageRange',
                header: 'Source pages',
                meta: { align: 'right' as const, muted: true, numeric: true },
                // Display is the per-source span (a string like "1–8"), but sort
                // by the whole-case first-page index so the order stays numeric
                // and groups documents by source PDF in ingest order (a lexical
                // sort of the string would put "10–15" before "2–9").
                sortingFn: (a, b) =>
                    (a.original.firstPageIndex ?? 0) - (b.original.firstPageIndex ?? 0),
                cell: ({ row }) => row.original.pageRange ?? '—',
            },
            {
                id: 'documentDate',
                accessorKey: 'documentDateValue',
                header: 'Date',
                meta: { muted: true },
                cell: ({ row }) => {
                    const formatted = formatShortDocumentDate(row.original.document?.date);
                    if (!onDocumentDateChangeRef.current) {
                        return formatted ?? '—';
                    }
                    return <DatePickerCell row={row.original} onDateChange={onDocumentDateChangeRef.current} />;
                },
            },
            {
                id: 'uploadDate',
                accessorKey: 'uploadDate',
                header: 'Uploaded',
                meta: { muted: true },
                cell: ({ row }) => formatUploadDate(row.original.uploadDate) ?? '—',
            },
            {
                id: 'sourceFilename',
                accessorKey: 'sourceFilename',
                header: 'Source file',
                meta: { muted: true },
                cell: ({ row }) =>
                    row.original.sourceFilename ? (
                        <span title={row.original.sourceFilename} style={{ overflowWrap: 'anywhere' }}>
                            {row.original.sourceFilename}
                        </span>
                    ) : '—',
            },
            {
                id: 'lastEditedAt',
                accessorKey: 'lastEditedAt',
                header: 'Updated',
                meta: { muted: true },
                cell: ({ row }) =>
                    row.original.document.lastEditedBy || row.original.document.lastEditedAt ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            {row.original.document.lastEditedBy && <div>By {row.original.document.lastEditedBy}</div>}
                            {row.original.document.lastEditedAt && <div>{formatRelativeTime(row.original.document.lastEditedAt)}</div>}
                        </div>
                    ) : (
                        '—'
                    ),
            },
            {
                id: 'export',
                header: 'Export PDF',
                enableSorting: false,
                cell: ({ row }) => {
                    const disabled = disabledSetRef.current.has('export');
                    const handleClick = (event: React.MouseEvent | React.KeyboardEvent) => {
                        event.stopPropagation();
                        event.preventDefault();
                        if (disabled) {
                            return;
                        }
                        onDocumentActionRef.current?.('export', row.original.document.docId);
                    };
                    return (
                        <ExportButton
                            type="button"
                            aria-label={`Export ${row.original.document.title} as PDF`}
                            disabled={disabled}
                            onMouseDown={event => {
                                event.stopPropagation();
                                event.preventDefault();
                            }}
                            onClick={handleClick}
                            onKeyDown={event => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                    handleClick(event);
                                }
                            }}
                        >
                            <Download size={16} aria-hidden />
                        </ExportButton>
                    );
                },
            },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps -- columns use refs for callbacks; we only re-create columns when selection changes (so checkboxes re-render with new checked state) or when the on-page select-all callback identity changes.
        [selectedDocIdSet, onToggleSelectAllOnPage],
    );

    const pagination = useMemo(
        () => ({ pageIndex: paginationModel.page, pageSize: paginationModel.pageSize }),
        [paginationModel],
    );

    const table = useReactTable({
        data: rows,
        columns,
        getCoreRowModel: getCoreRowModel(),
        manualSorting: true,
        manualFiltering: true,
        manualPagination: true,
        state: {
            sorting,
            pagination,
            columnVisibility,
        },
        onSortingChange: handleSortingChange,
        rowCount,
    });

    const headerGroups = table.getHeaderGroups();
    const tableRows = table.getRowModel().rows;

    return (
        <TableWrapper style={{ position: 'relative' }}>
            {isLoading && <LoadingOverlay>Loading...</LoadingOverlay>}
            <ToolbarArea>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ maxWidth: 400 }}>
                        <SearchFilterBar
                            pills={filterPills}
                            onRemovePill={handleRemovePill}
                            placeholder="Search documents…"
                            searchText={searchQuery}
                            onSearchChange={onSearchQueryChange}
                            compact
                        />
                    </div>
                </div>
                <ToolbarRight>
                    {viewMode === 'full' && (
                        <ColumnMenuWrapper>
                            <ColumnMenuButton $open={columnMenuOpen} onClick={() => setColumnMenuOpen(o => !o)}>
                                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round">
                                    <path d="M1 3H11M1 6H11M1 9H11" />
                                    <circle cx="4" cy="3" r="1" fill="currentColor" />
                                    <circle cx="8" cy="6" r="1" fill="currentColor" />
                                    <circle cx="5" cy="9" r="1" fill="currentColor" />
                                </svg>
                                Columns
                            </ColumnMenuButton>
                            {columnMenuOpen && (
                                <>
                                    <div onClick={() => setColumnMenuOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 10 }} />
                                    <ColumnMenuPanel>
                                        {COLUMN_CONFIG.map(col => (
                                            <ColumnMenuItem
                                                key={col.id}
                                                $locked={col.locked}
                                                onClick={() => { if (!col.locked) toggleColumn(col.id); }}
                                            >
                                                <ColumnCheckbox $checked={!hiddenColumns.has(col.id)} $locked={col.locked}>
                                                    {!hiddenColumns.has(col.id) && (
                                                        <svg width="8" height="8" viewBox="0 0 10 10" fill="none">
                                                            <path d="M2 5L4.5 7.5L8 3" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                        </svg>
                                                    )}
                                                </ColumnCheckbox>
                                                {col.label}
                                            </ColumnMenuItem>
                                        ))}
                                    </ColumnMenuPanel>
                                </>
                            )}
                        </ColumnMenuWrapper>
                    )}
                    <ViewToggleGroup>
                        <ViewToggleButton $active={viewMode === 'compact'} onClick={() => handleViewModeChange('compact')} aria-label="Compact view">
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                                <path d="M2 4H12M2 7H12M2 10H12" />
                            </svg>
                        </ViewToggleButton>
                        <ViewToggleButton $active={viewMode === 'full'} onClick={() => handleViewModeChange('full')} aria-label="Full table view">
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                                <path d="M2 4H7M2 7H6M2 10H8" />
                                <rect x="9" y="3" width="3" height="2" rx="0.5" fill="currentColor" stroke="none" opacity="0.3" />
                                <rect x="9" y="6" width="3" height="2" rx="0.5" fill="currentColor" stroke="none" opacity="0.3" />
                                <rect x="9" y="9" width="3" height="2" rx="0.5" fill="currentColor" stroke="none" opacity="0.3" />
                            </svg>
                        </ViewToggleButton>
                    </ViewToggleGroup>
                </ToolbarRight>
            </ToolbarArea>
            <div style={{ display: 'flex', gap: 6, marginBottom: 12, marginTop: 4 }}>
                <div style={{ position: 'relative' }}>
                    <FilterChip label="Status" active={!!activeStatus} onClick={() => setStatusPopoverOpen(o => !o)} />
                    {statusPopoverOpen && (
                        <>
                            <div onClick={() => setStatusPopoverOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 50 }} />
                            <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 51, background: '#fff', border: '1px solid #e8e8e8', borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.1)', padding: 8, minWidth: 180 }}>
                                {[{ value: '', label: 'All statuses' }, ...statusOptions].map(opt => (
                                    <div
                                        key={opt.value}
                                        onClick={() => { onStatusFilterChange?.(opt.value || null); setStatusPopoverOpen(false); }}
                                        style={{ padding: '6px 10px', borderRadius: 5, cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: '#333' }}
                                        onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = '#f5f5f5'; }}
                                        onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                                    >
                                        {opt.label}
                                        {activeStatus === opt.value && <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="#111" strokeWidth="1.5" strokeLinecap="round"><path d="M3 6L5.5 8.5L9 4" /></svg>}
                                        {!activeStatus && !opt.value && <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="#111" strokeWidth="1.5" strokeLinecap="round"><path d="M3 6L5.5 8.5L9 4" /></svg>}
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>
                {onCategoryFilterChange && categoryTree.length > 0 && (
                    <div style={{ position: 'relative' }}>
                        <FilterChip label="Category" active={!!activeCategory} onClick={() => setCategoryPopoverOpen(o => !o)} />
                        {categoryPopoverOpen && (
                            <>
                                <div onClick={() => setCategoryPopoverOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 50 }} />
                                <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 51, background: '#fff', border: '1px solid #e8e8e8', borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.1)', padding: 8, minWidth: 240, maxHeight: 360, overflowY: 'auto' }}>
                                    {[{ value: '', label: 'All categories' }].map(opt => (
                                        <div
                                            key="all"
                                            onClick={() => { onCategoryFilterChange?.(null); setCategoryPopoverOpen(false); }}
                                            style={{ padding: '6px 10px', borderRadius: 5, cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: '#333' }}
                                            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = '#f5f5f5'; }}
                                            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                                        >
                                            {opt.label}
                                            {!activeCategory && <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="#111" strokeWidth="1.5" strokeLinecap="round"><path d="M3 6L5.5 8.5L9 4" /></svg>}
                                        </div>
                                    ))}
                                    {/* Flat category list — one row per category, no nesting. */}
                                    {categoryTree.map(section => (
                                        <div
                                            key={section.value}
                                            onClick={() => { onCategoryFilterChange?.(section.value); setCategoryPopoverOpen(false); }}
                                            style={{ padding: '6px 10px', borderRadius: 5, cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: '#333' }}
                                            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = '#f5f5f5'; }}
                                            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                                        >
                                            {section.label}
                                            {activeCategory === section.value && <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="#111" strokeWidth="1.5" strokeLinecap="round"><path d="M3 6L5.5 8.5L9 4" /></svg>}
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                )}
                <div style={{ position: 'relative' }}>
                    <FilterChip label="Date" active={!!(dateRange?.start || dateRange?.end)} onClick={() => setDatePopoverOpen(o => !o)} />
                    {datePopoverOpen && (
                        <>
                            <div onClick={() => setDatePopoverOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 50 }} />
                            <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 51, background: '#fff', border: '1px solid #e8e8e8', borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.1)', padding: '12px', minWidth: 220 }}>
                                <div style={{ marginBottom: 8 }}>
                                    <div style={{ fontSize: 11, color: '#999', fontWeight: 500, marginBottom: 3 }}>From</div>
                                    <input
                                        type="date"
                                        value={dateRange?.start ?? ''}
                                        onChange={e => onDateRangeChange?.({ ...dateRange, start: e.target.value || undefined })}
                                        style={{ width: '100%', padding: '4px 6px', border: '1px solid #e0e0e0', borderRadius: 4, fontSize: 12, fontFamily: 'inherit', color: '#333', boxSizing: 'border-box' }}
                                    />
                                </div>
                                <div style={{ marginBottom: 8 }}>
                                    <div style={{ fontSize: 11, color: '#999', fontWeight: 500, marginBottom: 3 }}>To</div>
                                    <input
                                        type="date"
                                        value={dateRange?.end ?? ''}
                                        onChange={e => onDateRangeChange?.({ ...dateRange, end: e.target.value || undefined })}
                                        style={{ width: '100%', padding: '4px 6px', border: '1px solid #e0e0e0', borderRadius: 4, fontSize: 12, fontFamily: 'inherit', color: '#333', boxSizing: 'border-box' }}
                                    />
                                </div>
                                <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                                    <button onClick={() => { onDateRangeChange?.(null); setDatePopoverOpen(false); }} style={{ padding: '4px 10px', border: '1px solid #e0e0e0', borderRadius: 4, background: '#fff', fontSize: 11, cursor: 'pointer', fontFamily: 'inherit', color: '#888' }}>Clear</button>
                                    <button onClick={() => setDatePopoverOpen(false)} style={{ padding: '4px 10px', border: 'none', borderRadius: 4, background: '#111', color: '#fff', fontSize: 11, cursor: 'pointer', fontFamily: 'inherit' }}>Apply</button>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
            {viewMode === 'compact' ? (
                <div style={{ borderTop: '1px solid #f0f0f0' }}>
                    {rows.length === 0 ? (
                        <NoRows message={emptyStateMessage} />
                    ) : (
                        rows.map(row => (
                            <CompactRow
                                key={row.id}
                                onClick={(event) => handleRowClick(row, event)}
                            >
                                <CompactDot $reviewed={row.document.status === 'reviewed'} />
                                <CompactTitle>{row.document.title}</CompactTitle>
                                {row.document.taxonomy && (
                                    <TaxonomyChip taxonomy={row.document.taxonomy} emptyFallback={null} maxWidth="140px" />
                                )}
                                {(row.document.lowConfidencePages ?? 0) > 0 && (
                                    <Badge label={`${row.document.lowConfidencePages} boundary`} type="quality" />
                                )}
                                {row.document.trailingBoundaryUncertain && !(row.document.lowConfidencePages ?? 0) && (
                                    <Badge label="Merge" type="quality" />
                                )}
                                {/* Duplicate-document badge intentionally hidden. */}
                                <CompactPages>{row.document.pageCount} pg</CompactPages>
                            </CompactRow>
                        ))
                    )}
                </div>
            ) : (
            <StyledTable>
                <Thead>
                    {headerGroups.map(headerGroup => (
                        <tr key={headerGroup.id}>
                            {headerGroup.headers.map(header => {
                                const canSort = header.column.getCanSort();
                                const sorted = header.column.getIsSorted();
                                const meta = header.column.columnDef.meta as
                                    | { align?: string }
                                    | undefined;
                                return (
                                    <Th
                                        key={header.id}
                                        $active={!!sorted}
                                        $sortable={canSort}
                                        $align={(meta?.align as 'left' | 'center' | 'right') ?? 'left'}
                                        onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                                    >
                                        {flexRender(header.column.columnDef.header, header.getContext())}
                                        {sorted && (
                                            <SortIndicator>
                                                {sorted === 'asc' ? ' \u25B2' : ' \u25BC'}
                                            </SortIndicator>
                                        )}
                                    </Th>
                                );
                            })}
                        </tr>
                    ))}
                </Thead>
                <Tbody>
                    {tableRows.length === 0 ? (
                        <EmptyRow>
                            <td colSpan={columns.length}>
                                <NoRows message={emptyStateMessage} />
                            </td>
                        </EmptyRow>
                    ) : (
                        tableRows.map(row => (
                            <Tr
                                key={row.id}
                                $clickable={!disabledSetRef.current.has('open')}
                                $active={row.original.id === selectedDocumentId}
                                onClick={event => handleRowClick(row.original, event)}
                            >
                                {row.getVisibleCells().map(cell => {
                                    const meta = cell.column.columnDef.meta as
                                        | { align?: string; numeric?: boolean; muted?: boolean }
                                        | undefined;
                                    return (
                                        <Td
                                            key={cell.id}
                                            $align={(meta?.align as 'left' | 'center' | 'right') ?? 'left'}
                                            $numeric={meta?.numeric}
                                            $muted={meta?.muted}
                                        >
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </Td>
                                    );
                                })}
                            </Tr>
                        ))
                    )}
                </Tbody>
            </StyledTable>
            )}
            <TablePagination
                page={paginationModel.page}
                pageSize={paginationModel.pageSize}
                totalRows={rowCount}
                pageSizeOptions={[10, 25, 50, 100]}
                onPageChange={page => onPaginationModelChange({ page, pageSize: paginationModel.pageSize })}
                onPageSizeChange={pageSize => onPaginationModelChange({ page: 0, pageSize })}
            />
        </TableWrapper>
    );
};
